import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';

import { useRecentActions } from '@components/features/Overview/QuickLinks/hook';
import { resourceUsageLimitsQuery } from '@queries/team';
import { api } from '@services/apiService';
import {
  func as functionResource,
  funcDeploymentDownload as functionDeploymentDownloadResource,
  funcDeployments as functionDeploymentsResource,
  funcInboundWhitelist as funcInboundWhitelistResource,
  funcInboundWhitelistIp as funcInboundWhitelistIpResource,
  funcOutboundWhitelist as funcOutboundWhitelistResource,
  funcOutboundWhitelistDomain as funcOutboundWhitelistDomainResource,
  funcRunToken as funcRunTokenResource,
  functions as functionsResource,
} from '@utils/resources/functions';

export const functionKeys = {
  scope: (teamUuid, appUuid) => ['functions', teamUuid, appUuid],
  functions: (teamUuid, appUuid) => [...functionKeys.scope(teamUuid, appUuid)],
  func: (teamUuid, appUuid, functionUuid) => [
    ...functionKeys.functions(teamUuid, appUuid),
    'function',
    functionUuid,
  ],
  deployments: (teamUuid, appUuid, functionUuid) => [
    ...functionKeys.func(teamUuid, appUuid, functionUuid),
    'deployments',
  ],
  deploymentDownload: (teamUuid, appUuid, functionUuid, deploymentId) => [
    ...functionKeys.deployments(teamUuid, appUuid, functionUuid),
    'download',
    deploymentId,
  ],
  repoBranches: (teamUuid, appUuid, functionUuid, isGithubFunction = false) => [
    ...functionKeys.func(teamUuid, appUuid, functionUuid),
    'repoBranches',
    isGithubFunction,
  ],
  repoStatus: (teamUuid, appUuid, functionUuid) => [
    ...functionKeys.func(teamUuid, appUuid, functionUuid),
    'repoStatus',
  ],
  notifications: (teamUuid, appUuid, functionUuid) => [
    ...functionKeys.func(teamUuid, appUuid, functionUuid),
    'notification',
  ],
  notification: (teamUuid, appUuid, functionUuid, configId) => [
    ...functionKeys.notifications(teamUuid, appUuid, functionUuid),
    configId,
  ],
};

const useRefreshFunctions = () => {
  const { teamUuid, appUuid } = useParams();
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: functionKeys.functions(teamUuid, appUuid),
    });
};

export const useRefreshFunction = () => {
  const { teamUuid, appUuid } = useParams();
  const queryClient = useQueryClient();

  return (funcUuid) =>
    queryClient.invalidateQueries({
      queryKey: functionKeys.func(teamUuid, appUuid, funcUuid),
    });
};

/**
 * @template [T=import('types').FunctionType[]]
 * @param {string} teamUuid
 * @param {string} appUuid
 * @param {import('@tanstack/react-query').UseQueryOptions<import('types').FunctionType[], unknown, T>} options
 * @returns {import('@tanstack/react-query').UseQueryOptions<import('types').FunctionType[], unknown, T>}
 */
export const functionsQuery = (teamUuid, appUuid, options) => ({
  queryKey: functionKeys.functions(teamUuid, appUuid),
  queryFn: async () =>
    (await api.get(functionsResource(teamUuid, appUuid)).json()).functions,
  ...(options ?? {}),
});

/**
 * @returns {import('@tanstack/react-query').UseQueryResult<import('types').FunctionType[]>}
 */
export function useFunctionsQuery(args = {}) {
  const { teamUuid, appUuid } = useParams();

  return useQuery({
    ...functionsQuery(teamUuid, appUuid),
    ...args,
  });
}

const _isTerminalState = (status) =>
  ['cancelled', 'failed', 'deployed'].includes(status);

export function functionQuery(teamUuid, appUuid, functionUuid) {
  return {
    queryKey: functionKeys.func(teamUuid, appUuid, functionUuid),
    queryFn: async () =>
      api.get(functionResource(teamUuid, appUuid, functionUuid)).json(),
  };
}

export function useFunctionQuery(functionUuid, args = {}) {
  const { teamUuid, appUuid } = useParams();
  const queryClient = useQueryClient();

  const query = useQuery({
    ...functionQuery(teamUuid, appUuid, functionUuid),
    enabled: functionUuid != null,
    refetchInterval: (functionData) =>
      !functionData?.latestDeployment?.deployedAt &&
      !_isTerminalState(functionData?.latestDeployment?.status)
        ? 2000
        : false,
    ...args,
  });

  useEffect(() => {
    if (!query.data) return;
    const { latestDeployment } = query.data;

    queryClient.setQueryData(
      functionKeys.deployments(functionUuid),
      (deploymentsData) => {
        if (!deploymentsData) return;

        return {
          ...deploymentsData,
          deployments: deploymentsData.deployments.map((deployment) => {
            if (deployment.id === latestDeployment?.id) {
              return { ...deployment, ...latestDeployment };
            }

            return deployment;
          }),
        };
      }
    );
  }, [queryClient, query.data]);

  return query;
}

export function useFunctionDeploymentsQuery(functionUuid) {
  const { teamUuid, appUuid } = useParams();

  return useQuery({
    queryKey: functionKeys.deployments(functionUuid),
    queryFn: async () => {
      return api
        .get(functionDeploymentsResource(teamUuid, appUuid, functionUuid))
        .json();
    },
    select: (data) => data.deployments,
  });
}

const editFunctionMutation = (teamUuid, appUuid) => ({
  mutationFn: async ({ funcUuid, funcData }) =>
    api
      .put(functionResource(teamUuid, appUuid, funcUuid), {
        json: funcData,
      })
      .json(),
});

export const useEditFunctionMutation = () => {
  const { teamUuid, appUuid } = useParams();
  const refreshFunctions = useRefreshFunctions();

  return useMutation({
    ...editFunctionMutation(teamUuid, appUuid),
    onSettled: async () => {
      await refreshFunctions();
    },
  });
};

const deleteFunctionMutation = (teamUuid, appUuid) => ({
  mutationFn: (funcUuid) =>
    api.delete(functionResource(teamUuid, appUuid, funcUuid)).json(),
});

export const useDeleteFunctionMutation = () => {
  const { teamUuid, appUuid } = useParams();
  const recents = useRecentActions();
  const queryClient = useQueryClient();

  return useMutation({
    ...deleteFunctionMutation(teamUuid, appUuid),
    onSuccess: (data, funcUuid) => {
      recents.remove({ type: 'function', id: funcUuid });
      void queryClient.invalidateQueries({
        queryKey: resourceUsageLimitsQuery(teamUuid).queryKey,
      });
    },
  });
};

const addIpsToFunctionInboundWhitelistMutation = (
  teamUuid,
  appUuid,
  functionUuid
) => ({
  mutationFn: async ({ addresses }) => {
    return api
      .post(funcInboundWhitelistResource(teamUuid, appUuid, functionUuid), {
        json: { addresses },
      })
      .json();
  },
});

export const useAddIpsToFunctionInboundWhitelistMutation = (functionUuid) => {
  const refreshFunction = useRefreshFunction();
  const { teamUuid, appUuid } = useParams();

  return useMutation({
    ...addIpsToFunctionInboundWhitelistMutation(
      teamUuid,
      appUuid,
      functionUuid
    ),
    onSettled: async () => refreshFunction(functionUuid),
  });
};

const deleteIpFromInboundWhitelistMutation = (
  teamUuid,
  appUuid,
  functionUuid
) => ({
  mutationFn: async (ipId) => {
    return api
      .delete(
        funcInboundWhitelistIpResource(teamUuid, appUuid, functionUuid, ipId)
      )
      .json();
  },
});

export const useDeleteIpFromInboundWhitelistMutation = (functionUuid) => {
  const refreshFunction = useRefreshFunction();
  const { teamUuid, appUuid } = useParams();

  return useMutation({
    ...deleteIpFromInboundWhitelistMutation(teamUuid, appUuid, functionUuid),
    onSettled: async () => refreshFunction(functionUuid),
  });
};

const addDomainsToFunctionOutboundWhitelistMutation = (
  teamUuid,
  appUuid,
  functionUuid
) => ({
  mutationFn: async ({ domains }) => {
    return api
      .post(funcOutboundWhitelistResource(teamUuid, appUuid, functionUuid), {
        json: { domains },
      })
      .json();
  },
});

export const useAddDomainsToFunctionOutboundWhitelistMutation = (
  functionUuid
) => {
  const refreshFunction = useRefreshFunction();
  const { teamUuid, appUuid } = useParams();

  return useMutation({
    ...addDomainsToFunctionOutboundWhitelistMutation(
      teamUuid,
      appUuid,
      functionUuid
    ),
    onSettled: async () => refreshFunction(functionUuid),
  });
};

const deleteDomainToFunctionOutboundWhitelistMutation = (
  teamUuid,
  appUuid,
  functionUuid
) => ({
  mutationFn: async (domainId) => {
    return api
      .delete(
        funcOutboundWhitelistDomainResource(
          teamUuid,
          appUuid,
          functionUuid,
          domainId
        )
      )
      .json();
  },
});

export const useDeleteDomainToFunctionOutboundWhitelistMutation = (
  functionUuid
) => {
  const refreshFunction = useRefreshFunction();
  const { teamUuid, appUuid } = useParams();

  return useMutation({
    ...deleteDomainToFunctionOutboundWhitelistMutation(
      teamUuid,
      appUuid,
      functionUuid
    ),
    onSettled: async () => refreshFunction(functionUuid),
  });
};

export const useDownloadDeployment = (deployment) => {
  const { teamUuid, appUuid } = useParams();
  const funcUuid = deployment.functionUuid;

  return useQuery({
    queryKey: functionKeys.deploymentDownload(
      teamUuid,
      appUuid,
      funcUuid,
      deployment.id
    ),
    queryFn: async () => {
      return api
        .get(
          functionDeploymentDownloadResource(
            teamUuid,
            appUuid,
            funcUuid,
            deployment.id
          )
        )
        .json();
    },
  });
};

const createRunTokenMutation = (teamUuid, appUuid, functionUuid) => ({
  mutationFn: async (payload) => {
    return api
      .post(funcRunTokenResource(teamUuid, appUuid, functionUuid), {
        json: payload,
      })
      .json();
  },
});

export const useCreateRunTokenMutation = (functionUuid) => {
  const { teamUuid, appUuid } = useParams();

  return useMutation({
    ...createRunTokenMutation(teamUuid, appUuid, functionUuid),
  });
};
